/** Juntar vários IFC num só, com o motor de fusão do liteAECO.
 *
 *  O motor é o worker deles (js/ifc-worker.js, Apache-2.0, © 2026 Luis
 *  Torrecilla), incluído SEM alterações em frontend/vendor/liteaeco e
 *  fixado num commit (docs/origens.json). Actualiza-se com
 *  `node scripts/origens.mjs --actualizar liteaeco-fusao`. Este ficheiro
 *  é o nosso lado: a orquestração, portada do tools/ifc-merger.html
 *  deles (também Apache-2.0), e as escolhas que expomos.
 *
 *  Como funciona o motor: trabalha sobre o TEXTO dos ficheiros, sem
 *  geometria. O primeiro ficheiro é o principal; cada um dos outros é
 *  lido num worker à parte, os ids são deslocados para não colidirem,
 *  os IfcSite/IfcBuilding/IfcBuildingStorey juntam-se aos do principal
 *  conforme as opções, e no fim escreve-se um ficheiro só.
 *
 *  O que NÃO expomos, de propósito:
 *  - a "compressão profunda": vai buscar o web-ifc ao unpkg em tempo de
 *    execução, e a regra do hub é nada de CDN em execução;
 *  - arredondar coordenadas, tirar Psets, tirar a apresentação: perdem
 *    informação, e juntar ficheiros não deve perder nada.
 *
 *  Unidades: se um ficheiro estiver noutra unidade de comprimento, o
 *  motor converte-o para a do principal, pelas unidades DECLARADAS nos
 *  dois (não assume nada). O registo diz quantas medidas converteu e
 *  quais os tipos que não soube converter. */

/** Versão do protocolo do worker que este adaptador conhece. */
export const PROTOCOLO_DO_MOTOR = 8

export interface OpcoesDeFusao {
  /** Juntar os IfcSite num só, ou manter separados. */
  sitios: 'merge' | 'keep'
  edificios: 'merge' | 'keep'
  /** Juntar pisos pelo nome, pela cota, ou manter separados. */
  pisos: 'name' | 'elevation' | 'keep'
  /** IfcOwnerHistory: manter os de cada ficheiro ou usar o do principal. */
  historico: 'keep' | 'merge'
  /** Deduplicar recursos iguais (pontos, direcções, estilos). Não perde
   *  informação: troca repetidos por uma referência ao primeiro. Não
   *  mexe em GlobalIds (isso é `repararGlobalIds`). */
  reduzirRepeticoes: boolean
  /** Dar GlobalIds novos às entidades cujo GlobalId já apareceu noutro
   *  ficheiro (típico ao juntar duas versões do mesmo modelo). Sem isto
   *  o ficheiro junto fica com GlobalIds repetidos, o que é IFC inválido;
   *  com isto deixa de ser possível encontrar essas entidades pelo
   *  GlobalId antigo (BCF, comparações). Por isso é uma escolha. */
  repararGlobalIds: boolean
}

export const OPCOES_POR_OMISSAO: OpcoesDeFusao = {
  sitios: 'merge',
  edificios: 'merge',
  pisos: 'name',
  historico: 'keep',
  reduzirRepeticoes: false,
  repararGlobalIds: false,
}

/** O mínimo de um Worker de que precisamos (para os testes). */
export interface WorkerLike {
  postMessage(msg: unknown, transfer?: Transferable[]): void
  onmessage: ((e: { data: unknown }) => void) | null
  onerror: ((e: { message?: string }) => void) | null
  terminate(): void
}

export interface Registo {
  (linha: string, aviso?: boolean): void
}

interface Mensagem {
  type?: string
  reqId?: number
  proto?: number
  msg?: string
  warn?: boolean
  [k: string]: unknown
}

function urlDoMotor(): string {
  const base = (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/'
  return `${base}vendor/liteaeco/ifc-worker.js`
}

export function criarWorkerDoMotor(): WorkerLike {
  return new Worker(urlDoMotor()) as unknown as WorkerLike
}

/** As opções no formato do motor. As que perdem informação ficam
 *  sempre desligadas, e a compressão profunda nunca é pedida. */
export function opcoesDoMotor(o: OpcoesDeFusao, discoTemporario: boolean) {
  return {
    optSite: o.sitios,
    optBuilding: o.edificios,
    optStorey: o.pisos,
    optMeta: o.historico,
    // No motor, o `optLevel` do finalize só decide a reparação de
    // GlobalIds ('none' = só relatar, outro = regenerar); a deduplicação
    // de recursos é a `optResourceDedup`. Antes as duas iam juntas na
    // caixa "Repetições", e marcá-la trocava GlobalIds sem o dizer.
    optLevel: o.repararGlobalIds ? 'low' : 'none',
    optResourceDedup: o.reduzirRepeticoes,
    optDedupColors: false,
    optFloatPrecision: false,
    floatPrecisionVal: 6,
    optStripPresentation: false,
    optDeepCompress: false,
    optStripPsets: false,
    useScratchDisk: discoTemporario,
  }
}

function coordenador(criar: () => WorkerLike, registar: Registo) {
  const w = criar()
  let pronto: () => void
  let falhou: (e: Error) => void
  const promessaPronto = new Promise<void>((res, rej) => {
    pronto = res
    falhou = rej
  })
  const temporizador = setTimeout(() => falhou(new Error('O motor de fusão não arrancou em 8 s.')), 8000)
  const pendentes = new Map<number, { res: (d: Mensagem) => void; rej: (e: Error) => void }>()
  let seq = 0
  const falharTudo = (msg: string) => {
    const e = new Error(msg)
    for (const p of pendentes.values()) p.rej(e)
    pendentes.clear()
    falhou(e)
  }
  w.onmessage = (ev) => {
    const d = (ev.data ?? {}) as Mensagem
    if (d.type === 'ready') {
      clearTimeout(temporizador)
      if (d.proto !== PROTOCOLO_DO_MOTOR) {
        falharTudo(
          `O motor de fusão fala o protocolo ${d.proto} e este adaptador conhece o ${PROTOCOLO_DO_MOTOR}. O liteAECO mudou o worker: rever o adaptador antes de o usar.`,
        )
      } else pronto()
      return
    }
    if (d.type === 'log') {
      registar(String(d.msg ?? ''), Boolean(d.warn))
      return
    }
    const p = d.reqId !== undefined ? pendentes.get(d.reqId) : undefined
    if (!p) return
    pendentes.delete(d.reqId!)
    if (d.type === 'error') p.rej(new Error(String(d.msg ?? 'erro no motor de fusão')))
    else p.res(d)
  }
  w.onerror = (e) => {
    clearTimeout(temporizador)
    falharTudo(`O motor de fusão falhou: ${e?.message ?? 'sem mensagem'}`)
  }
  return {
    async chamar(action: string, payload: Record<string, unknown>, transfer?: Transferable[]): Promise<Mensagem> {
      await promessaPronto
      return new Promise((res, rej) => {
        const reqId = ++seq
        pendentes.set(reqId, { res, rej })
        w.postMessage({ action, reqId, ...payload }, transfer ?? [])
      })
    },
    terminar() {
      clearTimeout(temporizador)
      w.terminate()
    },
  }
}

function chamarFilho(w: WorkerLike, action: string, payload: Record<string, unknown>, registar: Registo): Promise<Mensagem> {
  return new Promise((res, rej) => {
    w.onmessage = (ev) => {
      const d = (ev.data ?? {}) as Mensagem
      if (d.type === 'ready') return
      if (d.type === 'log') {
        registar(String(d.msg ?? ''), Boolean(d.warn))
        return
      }
      if (d.type === 'error' || d.success === false) rej(new Error(String(d.msg ?? d.error ?? 'erro no motor de fusão')))
      else res(d)
    }
    w.onerror = (e) => rej(new Error(e?.message ?? 'o motor de fusão falhou'))
    w.postMessage({ action, ...payload })
  })
}

export interface ResultadoDaFusao {
  blob: Blob
  nome: string
  bytes: number
  /** Ficheiros que não entraram, com o motivo. */
  falhados: { nome: string; motivo: string }[]
  avisos: string[]
  /** Entidades com GlobalId repetido encontradas na fusão, e se foram
   *  reparadas (GlobalId novo) ou deixadas como estavam. */
  globalIdsRepetidos: number
  globalIdsReparados: boolean
}

/** Lê do registo do motor quantos GlobalIds repetidos houve. O motor
 *  não o devolve na resposta, só no registo; a frase está fixada pelo
 *  commit do worker (docs/origens.json) e um teste com o worker real
 *  falha se mudar. */
export function contarGlobalIdsRepetidos(linha: string): number | null {
  const m = linha.match(/(?:Found|regenerated) (\d+) duplicate GlobalId/)
  return m ? parseInt(m[1], 10) : null
}

/** Junta `outros` ao `principal`. Cada ficheiro secundário é lido num
 *  worker próprio, um de cada vez: para quem tem 8 GB de memória, dois
 *  ao mesmo tempo com modelos grandes é pedir para o browser rebentar. */
export async function juntarIfc(
  principal: File,
  outros: File[],
  opcoes: OpcoesDeFusao,
  registar: Registo,
  criar: () => WorkerLike = criarWorkerDoMotor,
): Promise<ResultadoDaFusao> {
  const total = [principal, ...outros].reduce((s, f) => s + f.size, 0)
  const memoriaGb = Math.min(8, Math.max(1, (globalThis.navigator as { deviceMemory?: number } | undefined)?.deviceMemory ?? 8))
  const discoTemporario = total >= memoriaGb * 20 * 1048576
  const o = opcoesDoMotor(opcoes, discoTemporario)
  const avisos: string[] = []
  const falhados: ResultadoDaFusao['falhados'] = []
  let globalIdsRepetidos = 0
  const reg: Registo = (l, aviso) => {
    const n = contarGlobalIdsRepetidos(l)
    if (n !== null) globalIdsRepetidos += n
    if (aviso) avisos.push(l)
    registar(l, aviso)
  }
  const coord = coordenador(criar, reg)
  try {
    const mestre = await coord.chamar('master', { file: principal, fileName: principal.name, options: o })
    let maxId = Number(mestre.maxId)
    const unidade = mestre.unit
    const esquema = String(mestre.schema ?? '')
    reg(`Principal: ${principal.name} (${esquema}, unidade de comprimento ${String(unidade)}).`)
    for (const f of outros) {
      const w = criar()
      try {
        const lido = await chamarFilho(w, 'childLoad', { file: f, fileName: f.name, options: o }, reg)
        if (lido.schema && lido.schema !== 'UNKNOWN' && esquema !== 'UNKNOWN' && lido.schema !== esquema) {
          reg(`${f.name} é ${String(lido.schema)} e o principal é ${esquema}. Junta-se na mesma, mas rever o resultado: entidades de esquemas diferentes podem não ser compatíveis.`, true)
        }
        const offset = maxId + 10000
        maxId = offset + Number(lido.maxId)
        const r = await chamarFilho(
          w,
          'childGraft',
          { offset, unit1: unidade, pTrees: mestre.pTrees, options: o, masterDictArray: mestre.masterDictArray },
          reg,
        )
        const escala = Number(r.scaleFactor ?? 1)
        reg(`${f.name}: junto${escala !== 1 ? `, convertido pela escala ${escala} (das unidades declaradas)` : ''}.`)
        if (Array.isArray(r.unscaledTypes) && r.unscaledTypes.length > 0) {
          reg(`${f.name}: ${r.unscaledTypes.length} tipo(s) com comprimentos que o motor não converteu (${r.unscaledTypes.join(', ')}). Rever à mão.`, true)
        }
        if (r.scaledMeasures) reg(`${f.name}: ${String(r.scaledMeasures)} medida(s) de propriedades e quantidades convertidas para a unidade do principal.`)
        const buffer = r.buffer as ArrayBuffer
        await coord.chamar('graft', { buffer, fileName: r.fileName, fileSize: f.size, contextIds: r.contextIds ?? [] }, [buffer])
      } catch (e) {
        const motivo = e instanceof Error ? e.message : String(e)
        falhados.push({ nome: f.name, motivo })
        reg(`${f.name} não entrou: ${motivo}`, true)
      } finally {
        w.terminate()
      }
    }
    const fim = await coord.chamar('finalize', { options: { optLevel: o.optLevel, optDedupColors: false, optFloatPrecision: false, floatPrecisionVal: 6, optStripPresentation: false } })
    const blob = new Blob(fim.parts as BlobPart[], { type: 'application/x-step' })
    return {
      blob,
      nome: `${principal.name.replace(/\.ifc$/i, '')}_junto.ifc`,
      bytes: Number(fim.bytes ?? blob.size),
      falhados,
      avisos,
      globalIdsRepetidos,
      globalIdsReparados: opcoes.repararGlobalIds,
    }
  } catch (e) {
    try {
      await Promise.race([coord.chamar('abort', {}), new Promise((r) => setTimeout(r, 2000))])
    } catch {
      // O motor já caiu: não há o que abortar.
    }
    throw e
  } finally {
    coord.terminar()
  }
}
